import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useRouteError, isRouteErrorResponse } from 'react-router-dom';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ForbiddenState } from '@/components/feedback/states';

/**
 * Every route gets an error boundary (docs/04 §6). A render failure in one screen must
 * not take the shell with it — the sidebar and topbar keep working so the user can
 * navigate away instead of reloading.
 */
const Fallback = ({ error, onReset }) => {
  const { t } = useTranslation('common');

  if (error?.status === 403) return <ForbiddenState />;

  return (
    <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
      <div className="flex size-11 items-center justify-center rounded-full bg-destructive/10 text-destructive">
        <AlertTriangle className="size-5" />
      </div>
      <div className="space-y-1">
        <p className="font-medium">{t('errors.title')}</p>
        <p className="max-w-md text-sm text-muted-foreground">{t('errors.boundary')}</p>
        {error?.message ? (
          <p className="max-w-md break-words font-mono text-caption text-muted-foreground/80">
            {error.message}
          </p>
        ) : null}
      </div>
      {onReset ? (
        <Button variant="outline" size="sm" onClick={onReset}>
          <RefreshCw className="size-4" />
          {t('actions.retry')}
        </Button>
      ) : null}
    </div>
  );
};

/** Class component because React still offers no hook for `componentDidCatch`. */
export class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error('Route render failed', error, info);
  }

  render() {
    if (this.state.error) {
      return <Fallback error={this.state.error} onReset={() => this.setState({ error: null })} />;
    }
    return this.props.children;
  }
}

/** The router's own `errorElement` — catches loader/route errors, not render errors. */
export const RouteErrorElement = () => {
  const error = useRouteError();
  const normalised = isRouteErrorResponse(error)
    ? { status: error.status, message: error.statusText }
    : error;

  return <Fallback error={normalised} />;
};

export default RouteErrorBoundary;
